import React, { useMemo, useState } from 'react';
import { Loader2, Settings, Sparkles, CheckCircle2, Copy } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useApiMutation, useQueryClient } from '../../shared/hooks/useApiQuery';
import { useProjectSettings } from '../../shared/hooks/useProjectSettings';
import { JiraFieldPicker, type RegisteredField } from '../../shared/components/JiraFieldPicker';

/**
 * Project Settings section: configure the Jira "Developer" and "QA"
 * user-picker custom fields per product. These two fields drive:
 *  - Forecast Sprint view grouping (dev swimlane rows / QA swimlane rows)
 *  - role-aware drag-and-drop write-back (dev row → Jira Developer field,
 *    QA row → Jira QA field; see `issuePlanningService`)
 *  - the `issues.developer` / `issues.qa` columns populated on every sync
 *    (see `issueJiraSync.mapJiraIssueToRow`).
 *
 * When unset, every story lands in the "Unassigned developers" / "Unassigned
 * QA" buckets and drag-and-drop to a dev/QA row throws. Auto-detection fills
 * empty keys on the first Jira sync when Jira has exactly one user-picker
 * custom field named "Developer" / "QA"; the manual "Auto-detect" button here
 * runs the same logic on demand. Configured values are never overwritten.
 */
export const JiraRoleFieldsSection: React.FC = () => {
  const { currentProduct } = useAuth();
  const productName = currentProduct?.name?.trim() || null;

  const settingsQuery = useProjectSettings();
  const data = (settingsQuery.data ?? {}) as Record<string, unknown>;
  const queryClient = useQueryClient();

  const devFieldId = typeof data.jiraDeveloperFieldId === 'string' ? data.jiraDeveloperFieldId : '';
  const qaFieldId = typeof data.jiraQAFieldId === 'string' ? data.jiraQAFieldId : '';

  // Per-row save state mirroring ForecastCustomFieldsSection's contract.
  const [devSaving, setDevSaving] = useState(false);
  const [qaSaving, setQaSaving] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error' | 'info'; text: string } | null>(
    null
  );
  const [detecting, setDetecting] = useState(false);

  const saveMutation = useApiMutation<{ success: boolean }, Record<string, string>>({
    path: '/api/project-settings',
  });

  const detectMutation = useApiMutation<
    {
      developerFieldId: string | null;
      qaFieldId: string | null;
      developerCandidates: Array<{ id: string; name: string }>;
      qaCandidates: Array<{ id: string; name: string }>;
      setDeveloper: boolean;
      setQa: boolean;
    },
    void
  >({
    path: '/api/jira-fields/auto-discover',
  });

  // Section-level "Apply to all products" (mirrors the Forecast custom-fields
  // pattern): copies this product's Developer/QA field ids to every other
  // product via POST /api/project-settings/apply-to-all.
  const applyAllMutation = useApiMutation<
    { appliedTo: number; appliedKeys: string[]; skippedKeys: string[] },
    { keys: string[] }
  >({
    path: '/api/project-settings/apply-to-all',
  });
  const [applyingAll, setApplyingAll] = useState(false);

  // useApiMutation invalidates ['/api/project-settings']; useProjectSettings
  // reads the ['project-settings'] cache, so refresh that key after every
  // successful write so the pickers reflect the new value immediately.
  const refreshProjectSettings = () => {
    queryClient.invalidateQueries({ queryKey: ['project-settings'] });
  };

  const persistField = async (key: 'jiraDeveloperFieldId' | 'jiraQAFieldId', value: string) => {
    const isDev = key === 'jiraDeveloperFieldId';
    if (isDev) setDevSaving(true);
    else setQaSaving(true);
    setMessage(null);
    try {
      await saveMutation.mutateAsync({ [key]: value });
      refreshProjectSettings();
      setMessage({
        kind: 'ok',
        text: `${isDev ? 'Developer' : 'QA'} field saved for ${productName ?? 'this product'}.`,
      });
    } catch (err) {
      setMessage({
        kind: 'error',
        text: err instanceof Error ? err.message : 'Failed to save field',
      });
    } finally {
      if (isDev) setDevSaving(false);
      else setQaSaving(false);
    }
  };

  const handleAutoDetect = async () => {
    setDetecting(true);
    setMessage(null);
    try {
      const result = await detectMutation.mutateAsync();
      refreshProjectSettings();
      const parts: string[] = [];
      if (result.setDeveloper) {
        parts.push(`Developer → ${result.developerFieldId}`);
      }
      if (result.setQa) {
        parts.push(`QA → ${result.qaFieldId}`);
      }
      if (parts.length > 0) {
        setMessage({ kind: 'ok', text: `Auto-detected: ${parts.join(', ')}.` });
      } else {
        const devCands = result.developerCandidates.length;
        const qaCands = result.qaCandidates.length;
        if (devCands === 0 && qaCands === 0) {
          setMessage({
            kind: 'info',
            text: 'No Jira user-picker custom fields named "Developer" or "QA" were found. Create them in Jira or pick a field manually below.',
          });
        } else {
          const bits: string[] = [];
          if (devCands > 1) bits.push(`${devCands} "Developer" fields`);
          if (qaCands > 1) bits.push(`${qaCands} "QA" fields`);
          setMessage({
            kind: 'info',
            text: `Multiple matches (${bits.join(', ')}). Pick the right one manually below — auto-detect only applies when exactly one field matches each role.`,
          });
        }
      }
    } catch (err) {
      setMessage({
        kind: 'error',
        text: err instanceof Error ? err.message : 'Auto-detect failed',
      });
    } finally {
      setDetecting(false);
    }
  };

  const handleApplyToAll = async () => {
    const parts: string[] = [];
    if (devFieldId) parts.push(`Developer → ${devFieldId}`);
    if (qaFieldId) parts.push(`QA → ${qaFieldId}`);
    if (
      !window.confirm(
        `Apply the Jira role fields of "${productName ?? 'the active product'}" to every product in the organization?\n\n` +
          (parts.length > 0
            ? `Current values:\n${parts.join('\n')}\n\n`
            : 'Neither field is configured on this product yet.\n\n') +
          `This overwrites the per-product Developer / QA field settings of every other product.`
      )
    ) {
      return;
    }
    setApplyingAll(true);
    setMessage(null);
    try {
      const out = await applyAllMutation.mutateAsync({
        keys: ['jiraDeveloperFieldId', 'jiraQAFieldId'],
      });
      refreshProjectSettings();
      const skipped =
        out.skippedKeys && out.skippedKeys.length > 0
          ? ` (skipped unset: ${out.skippedKeys.join(', ')})`
          : '';
      setMessage({
        kind: 'ok',
        text: `Applied Developer + QA fields to ${out.appliedTo} product(s).${skipped}`,
      });
    } catch (err) {
      setMessage({
        kind: 'error',
        text: err instanceof Error ? err.message : 'Apply-to-all failed',
      });
    } finally {
      setApplyingAll(false);
    }
  };

  // The picker expects "registered" fields. For role fields we want only
  // Jira user-picker custom fields; the picker's filterBySchemaType='user'
  // restricts the dropdown to those. Pass an empty registered list so the
  // picker's own catalog refresh + Jira fetch drive the options (the
  // "Registered in Forecast catalog" group still shows the org catalog
  // entries that survive the schema filter).
  const registeredFields: RegisteredField[] = useMemo(() => [], []);

  const renderTitle = (
    <h2
      className="section-title"
      style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}
    >
      <Settings className="label-icon" />
      <span>Jira role fields</span>
      {productName ? (
        <span
          title="The fields below apply to this product only. Switch product in the top bar to configure another."
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '2px 10px',
            borderRadius: 999,
            background: 'rgba(99,102,241,0.12)',
            border: '1px solid rgba(99,102,241,0.4)',
            color: 'var(--color-accent, #6366f1)',
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
          }}
        >
          Active product: {productName}
        </span>
      ) : null}
    </h2>
  );

  if (settingsQuery.isLoading) {
    return (
      <div className="card-section">
        {renderTitle}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 0' }}>
          <Loader2 className="animate-spin" size={16} />
          Loading...
        </div>
      </div>
    );
  }

  const configuredCount = (devFieldId ? 1 : 0) + (qaFieldId ? 1 : 0);

  return (
    <div className="card-section">
      {renderTitle}
      <p className="section-description" style={{ marginBottom: 16 }}>
        These two Jira user-picker fields drive the <strong>Forecast Sprint view</strong> grouping:{' '}
        stories are grouped into developer rows by the <strong>Developer</strong> field and into QA
        rows by the <strong>QA</strong> field. Drag-and-drop in the Sprint view writes back to the
        same fields in Jira (dev row → Developer, QA row → QA). They are stored per product in{' '}
        <strong>{productName ? `“${productName}”` : 'the active product'}</strong>; switch product
        in the top bar to configure another.{' '}
        <strong>
          Auto-detected on first Jira sync when a unique match exists; change here if needed.
        </strong>
      </p>

      {!productName ? (
        <div
          style={{
            padding: '8px 12px',
            borderRadius: 6,
            background: 'rgba(245,158,11,0.12)',
            color: '#f59e0b',
            fontSize: 13,
            marginBottom: 12,
          }}
          role="status"
        >
          No product selected. Pick a product in the top bar to configure its Jira role fields.
        </div>
      ) : null}

      {message ? (
        <div
          style={{
            padding: '8px 12px',
            borderRadius: 6,
            background:
              message.kind === 'error'
                ? 'rgba(239,68,68,0.15)'
                : message.kind === 'ok'
                  ? 'rgba(34,197,94,0.12)'
                  : 'rgba(99,102,241,0.12)',
            color:
              message.kind === 'error'
                ? '#ef4444'
                : message.kind === 'ok'
                  ? '#86efac'
                  : 'var(--color-accent, #6366f1)',
            fontSize: 13,
            marginBottom: 12,
          }}
        >
          {message.text}
          <button
            onClick={() => setMessage(null)}
            style={{
              marginLeft: 8,
              background: 'none',
              border: 'none',
              color: 'inherit',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            dismiss
          </button>
        </div>
      ) : null}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          marginBottom: 16,
        }}
      >
        <button
          type="button"
          onClick={handleAutoDetect}
          disabled={detecting || !productName}
          title="Scan Jira for user-picker custom fields named exactly 'Developer' and 'QA'. Only fills empty keys when exactly one match exists."
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 14px',
            borderRadius: 6,
            border: '1px solid var(--color-border, #555)',
            background: 'transparent',
            color: 'var(--color-text-secondary, #aaa)',
            cursor: detecting || !productName ? 'not-allowed' : 'pointer',
            fontSize: 13,
            opacity: detecting || !productName ? 0.6 : 1,
          }}
        >
          {detecting ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />}
          Auto-detect from Jira
        </button>
        <button
          type="button"
          onClick={handleApplyToAll}
          disabled={detecting || applyingAll || devSaving || qaSaving || !productName}
          title="Copy this product's Developer / QA field settings to every product in the organization"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 14px',
            borderRadius: 6,
            border: '1px solid var(--color-border, #555)',
            background: 'transparent',
            color: 'var(--color-text-secondary, #aaa)',
            cursor:
              detecting || applyingAll || devSaving || qaSaving || !productName
                ? 'not-allowed'
                : 'pointer',
            fontSize: 13,
            opacity: detecting || applyingAll || devSaving || qaSaving || !productName ? 0.6 : 1,
          }}
        >
          {applyingAll ? <Loader2 className="animate-spin" size={14} /> : <Copy size={14} />}
          Apply to all products
        </button>
        {configuredCount === 2 ? (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 12,
              color: '#86efac',
            }}
          >
            <CheckCircle2 size={14} /> Both fields configured
          </span>
        ) : (
          <span style={{ fontSize: 12, color: 'var(--color-text-secondary, #888)' }}>
            {configuredCount === 0
              ? 'Neither field is configured — Sprint view swimlanes will be empty until you set these.'
              : 'One field is still missing — set it so both dev and QA swimlanes populate.'}
          </span>
        )}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 16,
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Developer field</span>
            {devSaving ? <Loader2 className="animate-spin" size={12} /> : null}
          </div>
          <JiraFieldPicker
            value={devFieldId}
            onChange={(id) => void persistField('jiraDeveloperFieldId', id)}
            registeredFields={registeredFields}
            placeholder="Select the Jira Developer field…"
            clearOptionLabel="— None (clear) —"
            filterBySchemaType="user"
            description="Jira user-picker custom field used for developer swimlane rows and dev-row drag-and-drop write-back."
          />
        </div>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>QA field</span>
            {qaSaving ? <Loader2 className="animate-spin" size={12} /> : null}
          </div>
          <JiraFieldPicker
            value={qaFieldId}
            onChange={(id) => void persistField('jiraQAFieldId', id)}
            registeredFields={registeredFields}
            placeholder="Select the Jira QA field…"
            clearOptionLabel="— None (clear) —"
            filterBySchemaType="user"
            description="Jira user-picker custom field used for QA swimlane rows and QA-row drag-and-drop write-back."
          />
        </div>
      </div>
    </div>
  );
};

export default JiraRoleFieldsSection;
